import { styled } from "@mui/material/styles";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import { SubmitButton } from "@/common/components/buttons/submit-button/SubmitButton";
import { FormShell } from "@/common/components/forms/FormShell";
import { PasswordField } from "@/common/components/forms/specific/password-field/PasswordField";
import { TextField } from "@/common/components/forms/text-field/TextField";
import {
  useCheckEmailExistsMutation,
  useRegisterAccountMutation,
} from "@/shared/api/gateway/account/account.api";
import { useSnackbar } from "@/shared/containers/SnackbarProvider/SnackbarProvider";

import { getLoginErrorMessageId, useLoginMutation } from "../login/login.api";

import { LicenseAgreement } from "./LicenseAgreement";
import {
  NEW_ACCOUNT_INITIAL_VALUES,
  newAccountSchema,
  type NewAccountFormValues,
} from "./newAccount.schema";
import { getRegistrationErrorMessageId } from "./signup.api";

import type { FormikHelpers } from "formik";

const NewAccount = (): React.ReactElement => {
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const checkEmailExistsMutation = useCheckEmailExistsMutation();
  const registerAccountMutation = useRegisterAccountMutation();
  const loginMutation = useLoginMutation();

  const handleLogin = (email: string, password: string) => {
    loginMutation.mutate(
      { username: email, password },
      {
        onSuccess: () => navigate("/signup/import"),
        onError: (error) => {
          showSnackbar({
            isError: true,
            message: <FormattedMessage id={getLoginErrorMessageId(error)} />,
          });
        },
      },
    );
  };

  const handleRegister = (email: string, password: string) => {
    registerAccountMutation.mutate(
      { email, password },
      {
        onSuccess: () => handleLogin(email, password),
        onError: (error) => {
          showSnackbar({
            isError: true,
            message: (
              <FormattedMessage id={getRegistrationErrorMessageId(error)} />
            ),
          });
        },
      },
    );
  };

  const handleSubmit = (
    values: NewAccountFormValues,
    { setFieldError }: FormikHelpers<NewAccountFormValues>,
  ) => {
    const email = values.email.trim();
    const password = values.password.trim();

    checkEmailExistsMutation.mutate(email, {
      onSuccess: (exists) => {
        if (exists) {
          setFieldError("email", "NewAccount.emailExistsError");
          return;
        }

        handleRegister(email, password);
      },
    });
  };

  const pending =
    checkEmailExistsMutation.isPending ||
    registerAccountMutation.isPending ||
    loginMutation.isPending;

  return (
    <div>
      <Heading>
        <FormattedMessage id="NewAccount.heading" />
      </Heading>
      <Description>
        <FormattedMessage id="NewAccount.desc" />
      </Description>
      <FormShell<NewAccountFormValues>
        initialValues={NEW_ACCOUNT_INITIAL_VALUES}
        onSubmit={handleSubmit}
        validationSchema={newAccountSchema}
        footer={
          <SubmitButton data-testid="signup-submit" pending={pending}>
            <FormattedMessage id="NewAccount.toAccountCreate" />
          </SubmitButton>
        }
      >
        <TextField
          autoComplete="username"
          fullWidth
          inputMode="email"
          label={<FormattedMessage id="NewAccount.emailPlaceholder" />}
          name="email"
        />
        <PasswordField
          autoComplete="new-password"
          fullWidth
          label={<FormattedMessage id="NewAccount.passwordPlaceholder" />}
          name="password"
        />
        <LicenseAgreement />
      </FormShell>
    </div>
  );
};

const Heading = styled("h5")({
  fontWeight: 500,
  fontSize: 24,
  textAlign: "center",
  marginTop: 16,
  marginBottom: 8,
});

const Description = styled("p")({
  fontSize: 16,
  marginBottom: 16,
  textAlign: "center",
});

export { NewAccount };
